<template>
  <div class="rsm-app">

    <!-- Feed progress bar: reassures participants the feed has an end (reduces drop-out).
         Decoupled from the harmful-content timing so it doesn't reveal or bias the mechanism;
         holds below full until the "J'ai terminé" finish is actually available. -->
    <div v-if="screen === 'feed'" class="rsm-progress">
      <div class="rsm-progress__fill" :style="{ width: feedProgress + '%' }"></div>
    </div>

    <!-- Reveal questionnaire (T1 only) -->
    <reveal-screen
      v-if="screen === 'reveal'"
      :condition="resolvedCondition"
      :harmful-post-count="harmfulCount"
      @done="onRevealDone"
    />

    <!-- Feed (always rendered while screen === 'feed') -->
    <feed
      v-if="screen === 'feed'"
      :condition="resolvedCondition"
      :feed-type="resolvedFeedType"
      :shuffle="shuffle"
      :is-debug="isDebug"
      :video-lock="videoLockMode"
      :harmful-threshold-ms="harmfulThresholdMs"
      :paused="showChatbot || showInoculation"
      @post-viewed="onPostViewed"
      @harmful-time-updated="onHarmfulTimeUpdated"
      @trigger-chatbot="onTriggerChatbot"
      @interaction="onInteraction"
      @scroll-change="onScrollChange"
      @video-event="onVideoEvent"
    />

    <!-- T2 inoculation overlay — triggered at 4 min harmful threshold -->
    <inoculation-post
      v-if="showInoculation"
      :feed-type="resolvedFeedType"
      @inoculation-complete="onInoculationComplete"
    />

    <!-- J'ai terminé button — visible after 1 min total viewing (Phase 2) -->
    <!-- Label doubles as the "you can stop whenever you're ready" message per team decision (Slack, 10/07) -->
    <button
      v-if="screen === 'feed' && phase2TimeMs >= 60000"
      class="rsm-done-btn"
      @click="endSession"
    >
      J'ai terminé. Je passe à la suite de l'étude
    </button>

    <!-- Signaler un problème — floating button always visible during feed -->
    <button
      v-if="screen === 'feed'"
      class="rsm-signaler-btn"
      @click="showSignalerModal = true"
    >
      Signaler un problème
    </button>

    <!-- Signaler modal -->
    <div v-if="showSignalerModal" class="rsm-signaler-overlay" @click.self="closeSignalerModal">
      <div class="rsm-signaler-sheet">

        <template v-if="signalerStep === 'menu'">
          <div class="rsm-signaler-title">Signaler un problème</div>
          <button class="rsm-signaler-option" @click="signalerStep = 'not-well'">
            Je ne me sens pas bien
          </button>
          <button class="rsm-signaler-option" @click="signalerStep = 'technical'">
            J'ai un problème technique
          </button>
          <button class="rsm-signaler-cancel" @click="closeSignalerModal">Retour</button>
        </template>

        <template v-else-if="signalerStep === 'not-well'">
          <div class="rsm-signaler-title">On a bien noté</div>
          <p class="rsm-signaler-message">
            On a bien noté que tu ne te sens pas bien. Si tu souhaites arrêter de scroller, tu peux passer directement aux questions finales. Tu seras compensé(e) normalement.
          </p>
          <button class="rsm-signaler-option rsm-signaler-option--exit" @click="onSignalerExit">
            Je quitte la session de scrolling et je passe aux questions finales
          </button>
          <button class="rsm-signaler-option" @click="closeSignalerModal">
            Je continue la session de scrolling
          </button>
        </template>

        <template v-else-if="signalerStep === 'technical'">
          <div class="rsm-signaler-title">Problème technique</div>
          <p class="rsm-signaler-message">Décris ton problème :</p>
          <textarea
            v-model="signalerText"
            class="rsm-signaler-textarea"
            placeholder="Décris le problème que tu rencontres..."
            rows="4"
          ></textarea>
          <button
            class="rsm-signaler-option"
            :disabled="!signalerText.trim()"
            @click="onSignalerTechnical"
          >
            Envoyer
          </button>
          <button class="rsm-signaler-cancel" @click="signalerStep = 'menu'">Retour</button>
        </template>

      </div>
    </div>

    <!-- Post-exit support resources flow (triggered when participant says they don't feel well) -->
    <div v-if="screen === 'aide'" class="rsm-aide-screen">
      <div class="rsm-aide-card">
        <p class="rsm-aide-message">
          Si vous en avez besoin, nous vous proposons des ressources pour vous aider. Vous n'êtes pas obligé(e) de les consulter maintenant : elles vous seront proposées à nouveau à la fin de l'étude. Souhaitez-vous les consulter maintenant ?
        </p>
        <button class="rsm-aide-btn" @click="screen = 'ressources'">
          Oui, je veux consulter ces ressources maintenant
        </button>
        <button class="rsm-aide-btn rsm-aide-btn--secondary" @click="endSession">
          Non, je préfère continuer l'enquête et les avoir à la fin
        </button>
      </div>
    </div>

    <div v-if="screen === 'ressources'" class="rsm-aide-screen">
      <div class="rsm-aide-card rsm-aide-card--resources">
        <p class="rsm-aide-intro">Que tu aies ressenti un inconfort ou non pendant l'étude, voici des ressources gratuites et confidentielles, à garder de côté si un jour tu en as besoin, pour toi ou pour un proche :</p>
        <ul class="rsm-aide-list">
          <li><strong>SOS Crise</strong> — écoute et soutien psychologique : <strong>0800 19 00 00</strong> (lundi-samedi, 9h-19h)</li>
          <li><strong>Anorexie boulimie</strong>, info écoute : <strong>09 69 325 900</strong> (lundi, mardi, jeudi, vendredi, 16h-18h)</li>
          <li><strong>Enfine</strong>, écoute et entraide autour des troubles du comportement alimentaire : <strong>01 40 72 64 44</strong> (lundi et jeudi, 20h-22h) ou enfine@enfine.com</li>
          <li><strong>3018</strong>, numéro national pour les jeunes victimes de harcèlement ou violence numérique : 7j/7, 9h-23h</li>
          <li><strong>Psycom</strong> propose un annuaire de confiance qui recense des lignes d'écoute par thématique</li>
        </ul>
        <button class="rsm-aide-btn" @click="endSession">
          Continuer vers les questions finales
        </button>
      </div>
    </div>

    <!-- Chatbot overlay — sits above feed, only T3 -->
    <chatbot-overlay
      v-if="showChatbot"
      @chatbot-complete="onChatbotComplete"
    />

    <!-- End screen -->
    <end-screen
      v-if="screen === 'end'"
      :tracking-data="trackingData"
    />

    <!-- Debug panel — activated by ?debug=1 -->
    <div v-if="isDebug" class="rsm-debug">
      <button class="rsm-debug__toggle" @click="debugOpen = !debugOpen">
        {{ debugOpen ? '▼' : '▶' }} rsm debug
      </button>
      <div v-if="debugOpen" class="rsm-debug__panel">
        <pre>{{ JSON.stringify(trackingData, null, 2) }}</pre>
      </div>
    </div>

  </div>
</template>

<script>
import store from '../store.js';
import { setValue } from '../lib/predictiv.js';
import Feed from './Feed/Feed.vue';
import RevealScreen from './RevealScreen.vue';
import InoculationPost from './Feed/InoculationPost.vue';
import ChatbotOverlay from './Chatbot/ChatbotOverlay.vue';
import EndScreen from './EndScreen.vue';

const VALID_CONDITIONS  = ['control', 'T1', 'T2', 'T3'];
const VALID_FEED_TYPES  = ['female', 'male', 'general'];
const VALID_LOCK_MODES  = ['countdown', 'silent', 'none'];
const PHASE2_DURATION_MS = 60000; // 1 min of post-intervention scrolling before done button appears
const PROGRESS_TARGET_MS = 300000; // feed progress bar fills to 90% over ~5 min, then completes when the finish button is available
const DEFAULT_HARMFUL_THRESHOLD_MS = 240000; // Phase 1: 4 min of harmful content

function readParam(params, key, allowed, fallback) {
  const v = params.get(key);
  return allowed && allowed.includes(v) ? v : fallback;
}

// Testing-only override so QA doesn't have to sit through 4 real minutes to trigger an
// intervention. Absent or invalid -> unchanged default behaviour (this must never affect
// real participants, only ?thresholdSec=<seconds> in a test URL).
function readThresholdMs(params) {
  const seconds = parseInt(params.get('thresholdSec'), 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : DEFAULT_HARMFUL_THRESHOLD_MS;
}

function resolveNonBinaryFeed(params) {
  const val = params.get('choixNonBin');
  if (!val) return null;
  if (val === 'male' || val === 'boys') return 'male';
  if (val === 'female' || val === 'girls') return 'female';
  return null;
}

export default {
  components: {Feed, RevealScreen, InoculationPost, ChatbotOverlay, EndScreen },

  // Props are set by the Predictiv/PHP page template.
  // URL params take precedence over props (useful for local dev + QA).
  props: {
    condition: { type: String, default: 'control' },
    feedType:  { type: String, default: 'general' },
    test:      { type: String, default: '0' },
  },

  data() {
    const params = new URLSearchParams(window.location.search);
    const resolvedCondition = readParam(params, 'condition', VALID_CONDITIONS, this.condition || 'control');
    const nbFeedType = resolveNonBinaryFeed(params);
    const resolvedFeedType = nbFeedType || readParam(params, 'feedType', VALID_FEED_TYPES, this.feedType || 'general');
    const isDebug = params.get('debug') === '1' || this.test === '1';
    const shuffle = params.get('shuffle') !== '0';
    const videoLockMode = readParam(params, 'videoLock', VALID_LOCK_MODES, 'countdown');
    const harmfulThresholdMs = readThresholdMs(params);

    return {
      store,
      resolvedCondition,
      resolvedFeedType,
      isDebug,
      harmfulThresholdMs,
      debugOpen: true,

      shuffle,
      videoLockMode,

      screen: 'feed',
      revealShown: false,
      revealCompleted: false,
      showChatbot: false,
      showInoculation: false,
      inoculationTriggered: false,
      inoculationCompleted: false,
      sessionStartTime: Date.now(),

      // Phase 2: scrolling time after intervention completes
      phase2Active: false,
      phase2TimeMs: 0,
      totalFeedTimeMs: 0,

      // Signaler un problème
      showSignalerModal: false,
      signalerStep: 'menu',
      signalerText: '',

      // Tracking
      postsViewed: 0,
      harmfulCount: 0,
      harmfulTimeMs: 0,
      // Harmful/neutral posts viewed, split by whether the assigned intervention had
      // already run its course (phase2Active) at the moment each post was seen.
      harmfulPreCount: 0,
      harmfulPostCount: 0,
      neutralPreCount: 0,
      neutralPostCount: 0,
      nbScrollUp: 0,
      nbScrollDown: 0,
      videosVus: [],             // [{ videoId, ordre, timestamp }]
      videoEvents: [],           // [{ postId, videoId, timeStart, timeEnd, duration, skipped, completed, replayed }]
      tempsParVideoTotal: {},    // { videoId: totalSeconds }
      tempsParVideosCont: {},    // { videoId: maxContinuousSeconds }
      interactionEvents: [],     // { type, postId, timestamp, value? }
      chatbotTriggered: false,
      chatbotCompleted: false,
      chatbotAnswers: [],
      exerciseSelected: null,
      exerciseCompleted: false,
      interactionAgent: null,
      quizAnswers: null,
    };
  },

  computed: {
    // Feed progress bar percentage. Fills to 90% over PROGRESS_TARGET_MS of foreground feed time,
    // then completes to 100% only once the finish button is available, so it never shows "done"
    // while the participant is still locked into the feed.
    feedProgress() {
      if (this.phase2TimeMs >= PHASE2_DURATION_MS) return 100;
      return Math.min(90, Math.round((this.totalFeedTimeMs / PROGRESS_TARGET_MS) * 90));
    },
    trackingData() {
      return {
        condition:            this.resolvedCondition,
        feedType:             this.resolvedFeedType,
        postsViewed:          this.postsViewed,
        nbScrollUp:           this.nbScrollUp,
        nbScrollDown:         this.nbScrollDown,
        videosVus:            this.videosVus,
        videoEvents:          this.videoEvents,
        tempsParVideoTotal:   this.tempsParVideoTotal,
        tempsParVideosCont:   this.tempsParVideosCont,
        harmfulPostsViewed:   this.harmfulCount,
        harmfulPreCount:      this.harmfulPreCount,
        harmfulPostCount:     this.harmfulPostCount,
        neutralPreCount:      this.neutralPreCount,
        neutralPostCount:     this.neutralPostCount,
        harmfulTimeMs:        this.harmfulTimeMs,
        timeInFeedMs:         this.totalFeedTimeMs,
        chatbotTriggered:     this.chatbotTriggered,
        chatbotCompleted:     this.chatbotCompleted,
        chatbotAnswers:       this.chatbotAnswers,
        exerciseSelected:     this.exerciseSelected,
        exerciseCompleted:    this.exerciseCompleted,
        interactionAgent:     this.interactionAgent,
        quizAnswers:          this.quizAnswers,
        interactionEvents:    this.interactionEvents,
        clicLiker:     this.interactionEvents.filter(e => e.type === 'like').map(e => ({ postId: e.postId, timestamp: e.timestamp })),
        clicCommenter: this.interactionEvents.filter(e => e.type === 'comment').map(e => ({ postId: e.postId, timestamp: e.timestamp })),
        clicPartager:  this.interactionEvents.filter(e => e.type === 'share').map(e => ({ postId: e.postId, timestamp: e.timestamp })),
        clicSignaler:  this.interactionEvents.filter(e => e.type === 'report').map(e => ({ postId: e.postId, timestamp: e.timestamp })),

        // Intervention engagement (any arm)
        interventionShown:     this.revealShown || this.inoculationTriggered || this.chatbotTriggered,
        interventionCompleted: this.revealCompleted || this.inoculationCompleted || this.chatbotCompleted,

        // "Signaler un problème" as flat fields (also kept in interactionEvents)
        signalerExit:      this.interactionEvents.some(e => e.type === 'signaler_exit'),
        signalerExitTime:  (this.interactionEvents.find(e => e.type === 'signaler_exit') || { timestamp: null }).timestamp,
        signalerTechnique: (this.interactionEvents.find(e => e.type === 'signaler_technique') || {}).value || '',
      };
    }
  },

  mounted() {
    store.condition = this.resolvedCondition;
    store.feedType  = this.resolvedFeedType;
    store.isTest    = this.isDebug;
    store.sessionStartTime = this.sessionStartTime;

    // Accumulate total feed viewing time using real elapsed time (not a fixed +500),
    // so backgrounding the tab (which throttles setInterval) can't make total feed time
    // drift below harmful time. Only counts while the feed is actually on screen and visible.
    this._lastFeedTick = null;
    this._feedTimer = setInterval(() => {
      const active = this.screen === 'feed' && !this.showChatbot && !this.showInoculation && !document.hidden;
      const now = Date.now();
      if (active && this._lastFeedTick != null) {
        const delta = now - this._lastFeedTick;
        this.totalFeedTimeMs += delta;
        if (this.phase2Active) this.phase2TimeMs += delta;
      }
      this._lastFeedTick = active ? now : null;
    }, 500);

    // Flush tracking to Predictiv every 30 s so drop-outs keep partial data.
    this._flushTimer = setInterval(() => this._flushTracking(), 30000);
    this._visibilityHandler = () => {
      if (document.visibilityState === 'hidden') this._flushTracking();
    };
    document.addEventListener('visibilitychange', this._visibilityHandler);
  },

  beforeDestroy() {
    clearInterval(this._feedTimer);
    clearInterval(this._flushTimer);
    document.removeEventListener('visibilitychange', this._visibilityHandler);
  },

  methods: {
    _flushTracking() {
      const d = this.trackingData;
      setValue('rsm_condition',            d.condition || '');
      setValue('rsm_feed_type',            d.feedType || '');
      setValue('rsm_posts_viewed',         d.postsViewed || 0);
      setValue('rsm_harmful_posts_viewed', d.harmfulPostsViewed || 0);
      setValue('number_harmful_video_viewed_pre_intervention',  d.harmfulPreCount || 0);
      setValue('number_harmful_video_viewed_post_intervention', d.harmfulPostCount || 0);
      setValue('number_neutral_video_viewed_pre_intervention',  d.neutralPreCount || 0);
      setValue('number_neutral_video_viewed_post_intervention', d.neutralPostCount || 0);
      setValue('rsm_time_in_feed_ms',      d.timeInFeedMs || 0);
      setValue('rsm_chatbot_triggered',    d.chatbotTriggered ? 1 : 0);
      setValue('rsm_chatbot_completed',    d.chatbotCompleted ? 1 : 0);
      setValue('rsm_exercise_selected',    d.exerciseSelected || '');
      setValue('rsm_exercise_completed',   d.exerciseCompleted ? 1 : 0);
      setValue('rsm_chatbot_answers',      JSON.stringify(d.chatbotAnswers || []));
      setValue('rsm_scroll_up',            d.nbScrollUp || 0);
      setValue('rsm_scroll_down',          d.nbScrollDown || 0);
      // Clamp: harmful exposure is a subset of feed time, so it can never exceed it.
      // Guards the dataset against any residual timer drift producing impossible values.
      setValue('rsm_harmful_time_ms',      Math.min(d.harmfulTimeMs || 0, d.timeInFeedMs || 0));

      // Intervention engagement flags for every arm (T1 reveal, T2 inoculation, T3 chatbot),
      // so we can confirm each intervention was shown/completed, not just the chatbot arm.
      setValue('rsm_intervention_shown',     d.interventionShown ? 1 : 0);
      setValue('rsm_intervention_completed', d.interventionCompleted ? 1 : 0);

      // Ask-reveal quiz (T1 only): the participant's guess vs. the actual pre-intervention
      // harmful-post count (number_harmful_video_viewed_pre_intervention) so we can tell
      // whether they guessed correctly.
      setValue('rsm_quiz_q1_guess',  d.quizAnswers ? d.quizAnswers.q1Guess : '');
      setValue('rsm_quiz_q2_answer', d.quizAnswers ? d.quizAnswers.q2Answer : '');

      // Dedicated "Signaler un problème" columns (previously only inside the interaction_events JSON).
      setValue('rsm_signaler_exit',        d.signalerExit ? 1 : 0);
      setValue('rsm_signaler_exit_time',   d.signalerExitTime != null ? d.signalerExitTime : '');
      setValue('rsm_signaler_technique',   d.signalerTechnique || '');
      setValue('rsm_videos_viewed',        JSON.stringify(d.videosVus || []));
      setValue('rsm_video_events',         JSON.stringify(d.videoEvents || []));
      setValue('rsm_time_per_video',       JSON.stringify(d.tempsParVideoTotal || {}));
      setValue('rsm_time_per_video_cont',  JSON.stringify(d.tempsParVideosCont || {}));
      setValue('rsm_interaction_events',   JSON.stringify(d.interactionEvents || []));
      setValue('rsm_likes',                JSON.stringify(d.clicLiker || []));
      setValue('rsm_comments',             JSON.stringify(d.clicCommenter || []));
      setValue('rsm_shares',               JSON.stringify(d.clicPartager || []));
      setValue('rsm_reports',              JSON.stringify(d.clicSignaler || []));
    },
    onPostViewed(post) {
      this.postsViewed++;
      this.videosVus.push({
        videoId: post.youtubeId || post.id,
        ordre: this.postsViewed,
        timestamp: Date.now() - this.sessionStartTime,
      });

      // phase2Active flips true once the assigned intervention has run its course, so it's
      // the natural pre/post boundary: scrolling only resumes (or continues, for control)
      // after the intervention, since the intervention itself pauses the feed.
      if (post.harmful) {
        if (this.phase2Active) this.harmfulPostCount++;
        else this.harmfulPreCount++;
      } else {
        if (this.phase2Active) this.neutralPostCount++;
        else this.neutralPreCount++;
      }
    },
    onHarmfulTimeUpdated({ timeMs, count }) {
      this.harmfulTimeMs = timeMs;
      this.harmfulCount = count;
      if (this.resolvedCondition === 'T1' && !this.revealShown && timeMs >= this.harmfulThresholdMs) {
        this.revealShown = true;
        this.screen = 'reveal';
      }
      if (this.resolvedCondition === 'T2' && !this.inoculationTriggered && timeMs >= this.harmfulThresholdMs) {
        this.inoculationTriggered = true;
        this.showInoculation = true;
      }
      if (this.resolvedCondition === 'control' && !this.phase2Active && timeMs >= this.harmfulThresholdMs) {
        this.phase2Active = true;
      }
    },
    onVideoEvent(event) {
      this.videoEvents.push(event);
      const id = event.videoId;
      const durationSec = Math.round(event.duration / 1000);
      this.tempsParVideoTotal[id] = (this.tempsParVideoTotal[id] || 0) + durationSec;
      this.tempsParVideosCont[id] = Math.max(this.tempsParVideosCont[id] || 0, durationSec);
    },
    onInoculationComplete() {
      this.showInoculation = false;
      this.inoculationCompleted = true;
      this.phase2Active = true;
    },
    onScrollChange(direction) {
      if (direction === 'up') this.nbScrollUp++;
      else this.nbScrollDown++;
    },
    onTriggerChatbot() {
      if (this.chatbotTriggered) return;
      this.chatbotTriggered = true;
      this.showChatbot = true;
    },
    onChatbotComplete(result) {
      this.chatbotCompleted = true;
      this.chatbotAnswers = result.answers;
      this.exerciseSelected = result.exerciseSelected;
      this.exerciseCompleted = result.exerciseCompleted;
      this.interactionAgent = result.interactionAgent || null;
      this.showChatbot = false;
      this.screen = 'feed';
      this.phase2Active = true;
    },
    onInteraction(event) {
      this.interactionEvents.push(
        Object.assign({}, event, { timestamp: Date.now() - this.sessionStartTime })
      );
    },
    onRevealDone(answers) {
      if (answers) this.quizAnswers = answers;
      this.revealCompleted = true;
      this.screen = 'feed';
      this.phase2Active = true;
    },
    endSession() {
      this.screen = 'end';
    },
    closeSignalerModal() {
      this.showSignalerModal = false;
      this.signalerStep = 'menu';
      this.signalerText = '';
    },
    onSignalerExit() {
      this.onInteraction({ type: 'signaler_exit', postId: null });
      this.closeSignalerModal();
      this.screen = 'aide';
    },
    onSignalerTechnical() {
      const text = this.signalerText.trim();
      if (!text) return;
      this.onInteraction({ type: 'signaler_technique', postId: null, value: text });
      this.closeSignalerModal();
    },
  },
};
</script>
